import { fetchImageMeta } from '@/api/company';
import translator from '@/locale/translator';
import type { TemplateOptionDef } from '@/types/templateMeta';

// image option values look like { hash, focus? }; returns the hash when the value is one of those.
export function imageHash(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const hash = (value as Record<string, unknown>).hash;
  return typeof hash === 'string' ? hash : '';
}

// the same hash is often previewed by several rows/modals at once, so resolve each one only once.
const imagePreviewCache = new Map<string, Promise<string>>();

// preview (thumbnail) url for an image hash, empty string when the image cannot be resolved.
export function resolveImagePreviewUrl(hash: string): Promise<string> {
  let pending = imagePreviewCache.get(hash);
  if (!pending) {
    pending = fetchImageMeta(hash)
      .then((response) => response.data?.proxy?.url_thumbnail ?? response.data?.proxy?.url_preview ?? '')
      .catch(() => {
        // don't cache failures — a later render should be able to retry
        imagePreviewCache.delete(hash);
        return '';
      });
    imagePreviewCache.set(hash, pending);
  }
  return pending;
}

// normalize a benefit-style icon reference to the "variant/name" form loadIconSvg expects; a bare
// name defaults to the outline variant. returns null for inline SVG strings (rendered directly).
export function iconRef(icon: unknown): string | null {
  if (typeof icon !== 'string' || !icon || icon.trim().startsWith('<svg')) return null;
  return icon.includes('/') ? icon : `outline/${icon}`;
}

// whether a template-option value is "set" enough to be worth persisting / previewing.
export function hasValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    // image options look like { hash, focus? }; treat an empty hash as unset
    const obj = value as Record<string, unknown>;
    if ('hash' in obj) return !!obj.hash;
    return Object.keys(obj).length > 0;
  }
  return true;
}

// good enough for the scalar/array/plain-object shapes template option values take
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!hasValue(a) && !hasValue(b)) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

// whether any persistable option currently holds a value that differs from `baseline` (whatever
// was last saved or loaded, or empty for an untouched template) — used to decide whether the
// save-adjustment prompt actually has anything worth offering.
export function hasChangedFromBaseline(
  optionDefs: TemplateOptionDef[],
  current: Record<string, unknown>,
  baseline: Record<string, unknown>
): boolean {
  return optionDefs.some((def) => {
    const key = def.properties.key;
    return !!key && hasValue(current[key]) && !valuesEqual(current[key], baseline[key]);
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// suggested name for an adjustment the user did not name. `template` carries an {n} placeholder
// (e.g. "Adjustment {n}"); the number continues from the highest already-generated one on the same
// channel rather than the list length, so deleting one never causes its name to be handed out
// again. Names the user typed themselves don't match the template and so never affect the counter.
export function defaultAdjustmentName(existing: { name: string }[], template?: string): string {
  const nameTemplate = template ?? translator.get('publication.editor.adjustments.default_name');
  const pattern = new RegExp(`^${escapeRegExp(nameTemplate).replace('\\{n\\}', '(\\d+)')}$`);

  let highest = 0;
  for (const item of existing) {
    const match = pattern.exec((item.name ?? '').trim());
    if (match) highest = Math.max(highest, Number(match[1]));
  }

  return nameTemplate.replace('{n}', String(highest + 1));
}

// human-readable label for an option key, falling back to the key itself.
export function optionLabel(optionDefs: TemplateOptionDef[], key: string): string {
  const def = optionDefs.find((d) => d.properties.key === key);
  return def?.properties.label || key;
}

// compact, human-readable rendering of an option value for the preview lists.
export function formatOptionValue(value: unknown): string {
  if (!hasValue(value)) return translator.get('publication.editor.adjustments.no_value');
  if (typeof value === 'boolean') {
    return translator.get(
      value ? 'publication.editor.adjustments.value_yes' : 'publication.editor.adjustments.value_no'
    );
  }
  if (Array.isArray(value)) return `${value.length}`;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('hash' in obj) return String(obj.hash);
    return JSON.stringify(obj);
  }
  const str = String(value);
  return str.length > 80 ? `${str.slice(0, 80)}…` : str;
}
